import { collection, doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { AIResponse, Gap } from '../types';
import { AISystem } from '../config/ai';

/**
 * Gap Analysis Service
 * Maps poor AI responses to root cause categories and generates
 * prioritized, actionable fix recommendations
 */

interface GapAnalysisResult {
  gaps: Gap[];
  totalGaps: number;
  highPriorityGaps: number;
}

export class GapAnalysisService {
  /**
   * Analyze audit responses and identify gaps
   */
  async analyzeGaps(auditRunId: string, responses: AIResponse[]): Promise<GapAnalysisResult> {
    const gaps: Gap[] = [];

    // Group responses by issue type
    const lowScoreResponses = responses.filter((r) => r.sentimentScore < 50);
    const notMentionedResponses = responses.filter((r) => !r.mentioned);
    const inaccurateResponses = responses.filter((r) =>
      r.extractedClaims.some((c) => c.isAccurate === false)
    );

    // Analyze: Brand not mentioned
    if (notMentionedResponses.length > responses.length * 0.3) {
      const gap = await this.createGap(
        auditRunId,
        'missing-content',
        `Brand not mentioned in ${notMentionedResponses.length} of ${responses.length} queries`,
        notMentionedResponses.map((r) => r.aiSystem),
        'high',
        'medium',
        'Create comprehensive brand description content with clear product offerings, use cases, and differentiators. Ensure content is published on high-authority pages with proper schema markup.'
      );
      gaps.push(gap);
    }

    // Analyze: Low sentiment
    if (lowScoreResponses.length > 0) {
      const gap = await this.createGap(
        auditRunId,
        'weak-schema',
        `Low sentiment scores detected in ${lowScoreResponses.length} responses`,
        this.getUniqueSystems(lowScoreResponses),
        'medium',
        'low',
        'Review and update brand messaging to emphasize strengths, customer success stories, and positive differentiators. Add FAQ schema with positive framing of common questions.'
      );
      gaps.push(gap);
    }

    // Analyze: Inaccurate claims
    if (inaccurateResponses.length > 0) {
      const criticalInaccuracies = inaccurateResponses.filter((r) =>
        r.extractedClaims.some((c) => c.severity === 'critical')
      );

      const gap = await this.createGap(
        auditRunId,
        'outdated-content',
        `${inaccurateResponses.length} inaccurate claims detected (${criticalInaccuracies.length} critical)`,
        this.getUniqueSystems(inaccurateResponses),
        criticalInaccuracies.length > 0 ? 'high' : 'medium',
        'high',
        'Update website content with current, accurate information. Prioritize high-authority pages (About, Products, Pricing). Add "Last Updated" dates and verify all factual claims.'
      );
      gaps.push(gap);
    }

    // Analyze: Competitor dominance
    const competitorMentionedResponses = responses.filter((r) =>
      r.rawResponse.toLowerCase().includes('competitor') ||
      r.rawResponse.toLowerCase().includes('alternative')
    );

    if (competitorMentionedResponses.length > responses.length * 0.5) {
      const gap = await this.createGap(
        auditRunId,
        'competitor-dominance',
        `Competitors mentioned more frequently than brand`,
        this.getUniqueSystems(competitorMentionedResponses),
        'high',
        'high',
        'Create competitive comparison content highlighting unique strengths. Publish case studies and differentiation content. Ensure product pages clearly articulate value propositions vs competitors.'
      );
      gaps.push(gap);
    }

    // Analyze: Missing structured data
    const poorStructureResponses = responses.filter(
      (r) => !r.mentioned && r.sentiment === 'neutral'
    );

    if (poorStructureResponses.length > 0) {
      const gap = await this.createGap(
        auditRunId,
        'no-structured-data',
        `Brand poorly structured for AI consumption in ${poorStructureResponses.length} cases`,
        this.getUniqueSystems(poorStructureResponses),
        'medium',
        'medium',
        'Implement Schema.org markup (Organization, Product, FAQ, Review). Create clear H1/H2 structure. Add meta descriptions with brand and product keywords. Publish JSON-LD structured data.'
      );
      gaps.push(gap);
    }

    // Save all gaps to Firestore
    for (const gap of gaps) {
      const gapRef = doc(collection(db, 'gaps'));
      await setDoc(gapRef, { ...gap, id: gapRef.id });
    }

    const highPriorityGaps = gaps.filter((g) => g.impact === 'high').length;

    return {
      gaps,
      totalGaps: gaps.length,
      highPriorityGaps,
    };
  }

  /**
   * Create a gap record
   */
  private async createGap(
    auditRunId: string,
    category: Gap['category'],
    description: string,
    affectedSystems: AISystem[],
    impact: Gap['impact'],
    effort: Gap['effort'],
    suggestedFix: string
  ): Promise<Gap> {
    // Calculate priority score (0-100)
    // impact × effort - higher impact and lower effort = higher priority
    const impactScore = impact === 'high' ? 100 : impact === 'medium' ? 60 : 30;
    const effortScore = effort === 'low' ? 100 : effort === 'medium' ? 60 : 30;
    const priorityScore = Math.round((impactScore * 0.7 + effortScore * 0.3));

    return {
      id: '', // Will be set when saved
      auditRunId,
      category,
      description,
      affectedSystems,
      priorityScore,
      impact,
      effort,
      suggestedFix,
      status: 'open',
      createdAt: Timestamp.now(),
    };
  }

  /**
   * Get unique AI systems from responses
   */
  private getUniqueSystems(responses: AIResponse[]): AISystem[] {
    const systems = new Set(responses.map((r) => r.aiSystem));
    return Array.from(systems);
  }

  /**
   * Get gap category metadata
   */
  static getGapCategoryInfo(category: Gap['category']): {
    name: string;
    color: string;
    icon: string;
  } {
    const categories = {
      'missing-content': {
        name: 'Missing Content',
        color: '#DC2626',
        icon: '📄',
      },
      'outdated-content': {
        name: 'Outdated Content',
        color: '#EA580C',
        icon: '⏰',
      },
      'weak-schema': {
        name: 'Weak Schema',
        color: '#D97706',
        icon: '🏷️',
      },
      'low-authority': {
        name: 'Low Authority',
        color: '#CA8A04',
        icon: '⭐',
      },
      'poor-source-diversity': {
        name: 'Poor Source Diversity',
        color: '#65A30D',
        icon: '🌐',
      },
      'no-structured-data': {
        name: 'No Structured Data',
        color: '#0891B2',
        icon: '📊',
      },
      'competitor-dominance': {
        name: 'Competitor Dominance',
        color: '#7C3AED',
        icon: '⚔️',
      },
      'pricing': {
        name: 'Pricing Inaccuracy',
        color: '#DC2626',
        icon: '💰',
      },
      'products': {
        name: 'Product Information',
        color: '#2563EB',
        icon: '📦',
      },
      'compliance': {
        name: 'Compliance Issue',
        color: '#EA580C',
        icon: '⚖️',
      },
    };

    return categories[category] || { name: category, color: '#6B7280', icon: '❓' };
  }
}

export const gapAnalysisService = new GapAnalysisService();
